import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Stack,
  styled,
  TextField,
  type TextFieldProps,
} from "~/common/mui";
import type { AutoSuggestionCustomer, PersonifyRequest } from "~/features/assistedService/types";
import { resolveAsmMessage } from "~/features/assistedService/utils/asmMessage";

const AUTOCOMPLETE_MIN_LENGTH = 3;

interface Props {
  pending: boolean;
  alert: { messageCode: string; alertClass: string | null } | null;
  onPersonify: (request: PersonifyRequest) => void;
  onSearchCustomers: (customerId: string, signal?: AbortSignal) => Promise<AutoSuggestionCustomer[]>;
  className?: string;
  sx?: object;
}

interface AsmCustomerOptionProps {
  option: AutoSuggestionCustomer;
}

/**
 * Keeps the legacy ASM field order while giving each value a stable column. The mobile rule keeps
 * the same DOM order but lets the fields wrap as intentional two-column rows instead of allowing
 * each value to choose its own line.
 */
const AsmCustomerOptionRow = styled("span")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns:
    "minmax(4.5rem, 0.7fr) minmax(10rem, 1.4fr) minmax(9rem, 1.2fr) minmax(11rem, 1.5fr) minmax(5rem, 0.7fr)",
  gap: theme.spacing(1),
  alignItems: "center",
  width: "100%",
  minWidth: 0,
  [theme.breakpoints.down("tablet")]: {
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
    rowGap: theme.spacing(0.5),
  },
}));

const AsmCustomerOptionCell = styled("span")({
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const optionCell = (field: string, value?: string) => (
  <AsmCustomerOptionCell data-asm-field={field} title={value || undefined}>
    {value ?? ""}
  </AsmCustomerOptionCell>
);

/** Pure option presentation, exported for focused layout/contract tests. */
export const AsmCustomerOption = ({ option }: AsmCustomerOptionProps) => (
  <AsmCustomerOptionRow data-testid="asm-customer-option">
    {optionCell("accountDisplayUid", option.accountDisplayUid)}
    {optionCell("accountName", option.accountName)}
    {optionCell("value", option.value)}
    {optionCell("email", option.email)}
    {optionCell("customerType", option.customerType)}
  </AsmCustomerOptionRow>
);

/**
 * ASM customer emulation form. The customer autocomplete uses the injected lookup, whose
 * `AutoSuggestionCustomerData` shape (`value` = display name, `email` = customer id, `accountCode`
 * = account id, `carts` = existing carts) is mirrored in {@link AutoSuggestionCustomer}. On select,
 * the cartId field is driven from `carts` (1 → auto-fill, >1 → dropdown, none → disabled), matching
 * the legacy ASM jQuery UI handler.
 */
export const AsmEmulateForm = ({ pending, alert, onPersonify, onSearchCustomers, className, sx }: Props) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState<AutoSuggestionCustomer[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [selected, setSelected] = useState<AutoSuggestionCustomer | null>(null);
  const [cartId, setCartId] = useState("");
  const [cartOptions, setCartOptions] = useState<string[]>([]);
  const [noCarts, setNoCarts] = useState(false);
  const requestIdRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => abortControllerRef.current?.abort();
  }, []);

  const onInputChange = (_event: unknown, value: string, reason: string) => {
    setInputValue(value);
    if (reason !== "input") {
      return;
    }

    const customerId = value.trim();
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    abortControllerRef.current?.abort();

    if (customerId.length < AUTOCOMPLETE_MIN_LENGTH) {
      setSuggestions([]);
      setIsFetching(false);
      return;
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;
    setIsFetching(true);

    void onSearchCustomers(customerId, controller.signal)
      .then((results) => {
        if (requestIdRef.current === requestId) {
          setSuggestions(results);
        }
      })
      .catch((error: unknown) => {
        if (error instanceof Error && error.name === "AbortError") {
          return;
        }
        console.error("ASM: Failed to query customer autocomplete.", error);
        if (requestIdRef.current === requestId) {
          setSuggestions([]);
        }
      })
      .finally(() => {
        if (requestIdRef.current === requestId) {
          setIsFetching(false);
        }
      });
  };

  const onChange = (_event: unknown, value: AutoSuggestionCustomer | null) => {
    setSelected(value);
    // Drive cartId from the selected customer's carts (legacy select handler ~lines 248-264).
    const carts = value?.carts;
    if (carts && carts.length === 1) {
      setCartId(carts[0]);
      setCartOptions([]);
      setNoCarts(false);
    } else if (carts && carts.length > 1) {
      setCartOptions(carts);
      setCartId("");
      setNoCarts(false);
    } else {
      setCartOptions([]);
      setCartId("");
      // Only the "No Existing Carts" disabled state applies once a customer is actually picked.
      setNoCarts(Boolean(value));
    }
  };

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    // email is the customer identifier submitted (legacy sets customerId from ui.item.email).
    if (pending || !selected?.email) {
      return;
    }
    onPersonify({
      customerId: selected.email,
      customerName: selected.value ?? inputValue,
      cartId: cartId.trim() || undefined,
      accountId: selected.accountCode,
    });
  };

  const customerError = alert?.alertClass?.includes("ASM_alert_customer") ?? false;
  const cartError = alert?.alertClass?.includes("ASM_alert_cart") ?? false;

  const renderCustomerInput = (params: TextFieldProps) => (
    <TextField
      {...params}
      label={t("asm.emulate.customer")}
      size="small"
      error={customerError}
      helperText={isFetching ? t("asm.emulate.loading") : undefined}
    />
  );

  // Rich option layout mirroring the legacy jQuery autocomplete _renderItem
  // (account display UID / account name / name / email / type cells).
  const renderOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: AutoSuggestionCustomer,
  ) => (
    <li {...props} key={option.email ?? option.value}>
      <AsmCustomerOption option={option} />
    </li>
  );

  return (
    <Stack className={className} sx={sx} component="form" onSubmit={submit} spacing={2}>
      {alert && <Alert severity="error">{resolveAsmMessage(t, alert.messageCode)}</Alert>}
      <Stack direction={{ phone: "column", tablet: "row" }} spacing={2} alignItems="center">
        <Autocomplete
          sx={{ flexGrow: 1, minWidth: 0 }}
          options={suggestions}
          getOptionLabel={(option) => (typeof option === "string" ? option : option.value ?? option.email ?? "")}
          isOptionEqualToValue={(option, value) => option.email === value.email}
          inputValue={inputValue}
          onInputChange={onInputChange}
          onChange={onChange}
          renderInput={renderCustomerInput}
          renderOption={renderOption}
          loading={isFetching}
          slotProps={{
            listbox: {
              sx: (theme) => ({
                maxHeight: theme.spacing(40),
                overflowY: "auto",
              }),
            },
          }}
          filterOptions={(option) => option} // just pass through all the options --- assume proper filtering is done in BE
        />
        {noCarts ? (
          <TextField
            label={t("asm.emulate.cartId")}
            value=""
            disabled
            placeholder={t("asm.emulate.noCarts")}
            size="small"
          />
        ) : cartOptions.length > 1 ? (
          <Autocomplete
            sx={{ minWidth: 160 }}
            freeSolo
            options={cartOptions}
            value={cartId}
            onInputChange={(_event, value) => setCartId(value)}
            renderInput={(params) => <TextField {...params} label={t("asm.emulate.cartId")} size="small" />}
          />
        ) : (
          <TextField
            label={t("asm.emulate.cartId")}
            value={cartId}
            onChange={(event) => setCartId(event.target.value)}
            error={cartError}
            size="small"
          />
        )}
        <Box>
          <Button type="submit" variant="contained" disabled={pending || !selected?.email}>
            {t("asm.emulate.startSession")}
          </Button>
        </Box>
      </Stack>
    </Stack>
  );
};
